import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LOCALES = ['ru', 'en', 'ky'] as const;
export type Locale = typeof SUPPORTED_LOCALES[number];
export const DEFAULT_LOCALE: Locale = 'ru';

/** The UI locale, outside React. Dates must follow the language the user picked,
 *  not the one their phone happens to be set to. Hooks use useLocale(). */
export function currentLocale(): Locale {
  return (i18n.language?.split('-')[0] as Locale) || DEFAULT_LOCALE;
}

// Guests get Russian unless they picked a language (?lang= or saved choice) or their
// phone is set to Kyrgyz. The audience is in Kyrgyzstan: an English phone doesn't mean
// the person doesn't read Russian, and English is one tap away in the switcher.
const detector = new LanguageDetector();
detector.addDetector({
  name: 'kyNavigator',
  lookup: () => {
    const langs = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [];
    return langs.some((l) => l?.toLowerCase().startsWith('ky')) ? 'ky' : undefined;
  },
});

// Namespaces are loaded on demand when a route chunk mounts (via useTranslation('ns')).
i18n
  .use(HttpBackend)
  .use(detector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: ['common'],
    defaultNS: 'common',
    backend: { loadPath: '/locales/{{lng}}/{{ns}}.json' },
    detection: {
      order: ['querystring', 'localStorage', 'kyNavigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'kgdb_locale',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    react: { useSuspense: true },
  });

// <html lang> follows the UI language (screen readers, search engines, hyphenation).
const syncHtmlLang = (lng?: string) => {
  if (typeof document !== 'undefined') document.documentElement.lang = (lng || DEFAULT_LOCALE).split('-')[0];
};
i18n.on('languageChanged', syncHtmlLang);
syncHtmlLang(i18n.language);

export default i18n;
